import { Route, Routes } from "react-router";

import { ActivityDetailPage } from "@/pages/activity-detail";
import { ActivityFeedPage } from "@/pages/activity-feed";
import { BlockedPage } from "@/pages/blocked";
import { BuyAmountPage } from "@/pages/buy-amount";
import { BuyConfirmPage } from "@/pages/buy-confirm";
import { BuySuccessPage } from "@/pages/buy-success";
import { DemoHomePage } from "@/pages/demo-home";
import { DemoPositionPage } from "@/pages/demo-position";
import { DemoStartPage } from "@/pages/demo-start";
import { DemoTransactionsPage } from "@/pages/demo-transactions";
import { DemoUnavailablePage } from "@/pages/demo-unavailable";
import { DemoWalletPage } from "@/pages/demo-wallet";
import { DepositAmountPage } from "@/pages/deposit-amount";
import { DepositAssetPage } from "@/pages/deposit-asset";
import { DepositNetworkPage } from "@/pages/deposit-network";
import { DepositStatusPage } from "@/pages/deposit-status";
import { HomePage } from "@/pages/home";
import { MyRobotDetailPage } from "@/pages/my-robot-detail";
import { MyRobotsPage } from "@/pages/my-robots";
import { NotFoundPage } from "@/pages/not-found";
import { NotificationsPage } from "@/pages/notifications";
import { LegalPage } from "@/pages/legal";
import { ProfileEditPage } from "@/pages/profile-edit";
import { ProfileLanguagePage } from "@/pages/profile-language";
import { ProfileNotificationsPage } from "@/pages/profile-notifications";
import { ProfilePrivacyPage } from "@/pages/profile-privacy";
import { ReferralPage } from "@/pages/referral";
import { SupportPage } from "@/pages/support";
import { RobotDetailPage } from "@/pages/robot-detail";
import { RobotsComparePage } from "@/pages/robots-compare";
import { ProfilePage } from "@/pages/profile";
import { RatingPage } from "@/pages/rating";
import { RobotsCatalogPage } from "@/pages/robots-catalog";
import { TransactionsPage } from "@/pages/transactions";
import { WithdrawAddressPage } from "@/pages/withdraw-address";
import { WithdrawAmountPage } from "@/pages/withdraw-amount";
import { WithdrawConfirmPage } from "@/pages/withdraw-confirm";
import { WithdrawStatusPage } from "@/pages/withdraw-status";
import { WalletOverviewPage } from "@/pages/wallet-overview";
import { useMode } from "@/shared/api";
import { ROUTES } from "@/shared/config";

import { AppLayout } from "./ui/AppLayout";
import { useRouteTransition } from "./ui/useRouteTransition";

/**
 * Все роуты регистрируются сразу, ещё до того как экраны написаны: иначе
 * ссылки между разделами (главная → мои роботы, каталог → покупка, покупка →
 * пополнение) до своей задачи вели бы в пустоту. Заглушек здесь больше нет:
 * с закрытием FE-10 все объявленные роуты ведут на настоящие экраны.
 *
 * Порядок статических сегментов важен: `/robots/compare` объявлен ДО
 * `/robots/:id`, `/activity/notifications` — до `/activity/:id`. React Router
 * ранжирует статику выше динамики сам, но полагаться на это молча не стоит —
 * порядок закреплён тестом роутинга.
 *
 * `location` приходит не из контекста, а из useRouteTransition(): на время
 * съёмки слепка для перехода дерево остаётся на предыдущем экране (FE-13).
 * Без явного пропса React выкинул бы старый экран мгновенно и анимировать
 * было бы нечего.
 *
 * **Демо-режим (доработки §3) переключает ЭКРАНЫ, а не адреса.** Пути те же:
 * человек, переключившийся в демо, остаётся в той же навигации, а на месте
 * реального экрана оказывается демо-экран. Это одно место вместо `if (isDemo)`
 * в пятнадцати страницах — и, что важнее, единственное место, где видно
 * целиком, чем демо-режим отличается от реального.
 *
 * Разделы, которых в демо нет, отдают заглушку с выходом в реальный режим:
 * §3.1 запрещает выводить демо-средства и переводить их в реальный баланс, а
 * рейтинг и реферальная программа считаются по реальным результатам.
 */
export function AppRoutes() {
  const location = useRouteTransition();
  const { isDemo } = useMode();

  /** Заглушка «в демо недоступно» вместо реального экрана. */
  const unavailable = (titleKey: string) => <DemoUnavailablePage titleKey={titleKey} />;

  return (
    <Routes location={location}>
      <Route element={<AppLayout />}>
        <Route path={ROUTES.home} element={isDemo ? <DemoHomePage /> : <HomePage />} />

        {/* Каталог и карточка робота в демо те же самые: список роботов один,
            и заводить ему второй экран значило бы поддерживать две витрины. */}
        <Route path={ROUTES.robots} element={<RobotsCatalogPage />} />
        <Route path={ROUTES.robotsCompare} element={<RobotsComparePage />} />
        <Route path="/robots/:id" element={<RobotDetailPage />} />
        <Route
          path="/robots/:id/buy"
          element={isDemo ? <DemoStartPage /> : <BuyAmountPage />}
        />
        <Route path="/robots/:id/buy/confirm" element={<BuyConfirmPage />} />
        <Route path="/robots/:id/buy/success" element={<BuySuccessPage />} />

        {/* В демо список позиций живёт на главной — отдельного экрана «мои
            роботы» там нет, позиций не больше `demo_max_positions`. */}
        <Route
          path={ROUTES.myRobots}
          element={isDemo ? <DemoHomePage /> : <MyRobotsPage />}
        />
        <Route
          path="/my-robots/:id"
          element={isDemo ? <DemoPositionPage /> : <MyRobotDetailPage />}
        />

        <Route
          path={ROUTES.wallet}
          element={isDemo ? <DemoWalletPage /> : <WalletOverviewPage />}
        />
        <Route
          path={ROUTES.depositAsset}
          element={isDemo ? unavailable("screens.deposit") : <DepositAssetPage />}
        />
        <Route
          path={ROUTES.depositNetwork}
          element={isDemo ? unavailable("screens.deposit") : <DepositNetworkPage />}
        />
        <Route
          path={ROUTES.depositAmount}
          element={isDemo ? unavailable("screens.deposit") : <DepositAmountPage />}
        />
        <Route path="/wallet/deposit/:id" element={<DepositStatusPage />} />
        <Route
          path={ROUTES.transactions}
          element={isDemo ? <DemoTransactionsPage /> : <TransactionsPage />}
        />

        <Route
          path={ROUTES.withdrawAmount}
          element={isDemo ? unavailable("screens.withdraw") : <WithdrawAmountPage />}
        />
        <Route
          path={ROUTES.withdrawAddress}
          element={isDemo ? unavailable("screens.withdraw") : <WithdrawAddressPage />}
        />
        <Route
          path={ROUTES.withdrawConfirm}
          element={isDemo ? unavailable("screens.withdraw") : <WithdrawConfirmPage />}
        />
        <Route path="/wallet/withdraw/:id" element={<WithdrawStatusPage />} />

        <Route
          path={ROUTES.rating}
          element={isDemo ? unavailable("screens.rating") : <RatingPage />}
        />

        {/* Лента событий в демо — это история операций: заводить ради
            виртуальных начислений вторую таблицу событий на бэкенде не стали
            (см. докстринг `accrual/demo_job.py`), а показывать реальную ленту
            в демо нельзя — это и есть смешение контуров. */}
        <Route
          path={ROUTES.activity}
          element={isDemo ? <DemoTransactionsPage /> : <ActivityFeedPage />}
        />
        <Route path={ROUTES.notifications} element={<NotificationsPage />} />
        <Route path="/activity/:id" element={<ActivityDetailPage />} />

        <Route path={ROUTES.profile} element={<ProfilePage />} />
        {/* Имя и аватарка от контура не зависят: экран один и в демо, и в
            реальном режиме. */}
        <Route path={ROUTES.profileEdit} element={<ProfileEditPage />} />
        <Route path={ROUTES.profileNotifications} element={<ProfileNotificationsPage />} />
        <Route path={ROUTES.profileLanguage} element={<ProfileLanguagePage />} />
        <Route path={ROUTES.profilePrivacy} element={<ProfilePrivacyPage />} />
        <Route
          path={ROUTES.referral}
          element={isDemo ? unavailable("screens.referral") : <ReferralPage />}
        />
        <Route path={ROUTES.support} element={<SupportPage />} />
        <Route path="/legal/:slug" element={<LegalPage />} />

        <Route path={ROUTES.blocked} element={<BlockedPage />} />
        <Route path="*" element={<NotFoundPage />} />
      </Route>
    </Routes>
  );
}
